import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  onSnapshot,
} from "firebase/firestore";
import { db } from "./firebase";
import { writeUserNotification } from "./userNotifications";

function normalizeTag(tag) {
  return String(tag || "").trim().toLowerCase();
}

export async function createWeeklyQuestPrompt({
  title,
  prompt,
  tag,
  startDate,
  endDate,
  xpReward,
  commentsEnabled,
  adminUid,
}) {
  const cleanTag = String(tag || "").trim();
  await addDoc(collection(db, "weeklyQuestPrompts"), {
    title,
    prompt,
    tag: cleanTag,
    tagKey: normalizeTag(cleanTag),
    startAt: Timestamp.fromDate(new Date(startDate)),
    endAt: Timestamp.fromDate(new Date(endDate)),
    xpReward: Number(xpReward || 0),
    commentsEnabled: !!commentsEnabled,
    createdAt: serverTimestamp(),
    createdBy: adminUid,
  });
}

export async function getActivePromptForUser(userTag) {
  const userTagKey = normalizeTag(userTag);
  if (!userTagKey) return null;

  const snap = await getDocs(collection(db, "weeklyQuestPrompts"));
  if (snap.empty) return null;

  const now = new Date();

  const prompts = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  const activePrompts = prompts.filter((prompt) => {
    const promptTagKey = normalizeTag(prompt.tagKey || prompt.tag);
    if (promptTagKey !== userTagKey) return false;

    const start = prompt.startAt?.toDate?.();
    const end = prompt.endAt?.toDate?.();
    if (!start || !end) return false;
    return start <= now && end >= now;
  });

  if (activePrompts.length === 0) return null;

  // Return the most recently started active prompt
  activePrompts.sort((a, b) => {
    const aTime = a.startAt?.toDate?.()?.getTime?.() || 0;
    const bTime = b.startAt?.toDate?.()?.getTime?.() || 0;
    return bTime - aTime;
  });

  return activePrompts[0];
}

export async function ensureWeeklyQuestResponse(prompt, user) {
  const responseId = `${prompt.id}_${user.uid}`;
  const ref = doc(db, "weeklyQuestResponses", responseId);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    await setDoc(ref, {
      promptId: prompt.id,
      promptTitle: prompt.title || "",
      userId: user.uid,
      userName: user.name || "",
      userAvatar: user.avatar || "",
      tag: user.programTag || "",

      responseText: "",
      isSubmitted: false,
      isPrivate: false,
      status: "draft",

      xpAwarded: false,
      xpAwardedAt: null,
      submittedAt: null,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } else {
    // Backfill tag if it was missing when the doc was first created
    const existing = snap.data();
    if (!existing.tag && user.programTag) {
      await updateDoc(ref, { tag: user.programTag });
    }
  }

  const updatedSnap = await getDoc(ref);
  return { id: responseId, ...updatedSnap.data() };
}

export async function saveWeeklyQuestDraft(responseId, responseText, isPrivate) {
  await updateDoc(doc(db, "weeklyQuestResponses", responseId), {
    responseText,
    isPrivate,
    updatedAt: serverTimestamp(),
  });
}

export async function submitWeeklyQuest({ responseId, userId, responseText, xpReward, promptTitle, promptId = "", adminUid = "", userName = "", userAvatar = "" }) {
  const responseRef = doc(db, "weeklyQuestResponses", responseId);
  const userRef = doc(db, "users", userId);
  // Separate dedup doc — independent of the response's xpAwarded flag so bad-state data recovers
  const xpAwardedRef = doc(db, "users", userId, "xpAwardedQuests", responseId);
  let didAwardXp = false;

  // Include responseText in the transaction so there is no pending saveWeeklyQuestDraft
  // write on responseRef before the transaction reads it — that write would cause retries.
  await runTransaction(db, async (transaction) => {
    const responseSnap = await transaction.get(responseRef);
    if (!responseSnap.exists()) return;

    const xpAwardedSnap = await transaction.get(xpAwardedRef);
    const shouldAwardXp = !xpAwardedSnap.exists() && Number(xpReward || 0) > 0;

    const updates = {
      responseText: responseText ?? responseSnap.data().responseText ?? "",
      isSubmitted: true,
      status: "submitted",
      xpAwarded: shouldAwardXp || responseSnap.data().xpAwarded || false,
      submittedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    if (shouldAwardXp) {
      updates.xpAwardedAt = serverTimestamp();
      transaction.update(userRef, { xpAdjustmentTotal: increment(Number(xpReward)) });
      transaction.set(xpAwardedRef, { awardedAt: serverTimestamp(), amount: Number(xpReward), responseId });
      didAwardXp = true;
    }

    transaction.update(responseRef, updates);
  });

  // Resolve admin UID — prefer passed value, fall back to prompt's createdBy field
  let resolvedAdminUid = adminUid;
  if (!resolvedAdminUid && promptId) {
    try {
      const promptSnap = await getDoc(doc(db, "weeklyQuestPrompts", promptId));
      resolvedAdminUid = promptSnap.data()?.createdBy || "";
    } catch (_) { /* non-critical */ }
  }

  if (resolvedAdminUid && resolvedAdminUid !== userId) {
    writeUserNotification({
      toUserId: resolvedAdminUid,
      type: "comment",
      message: `${userName || "A client"} submitted their assignment${promptTitle ? ` "${promptTitle}"` : ""}`,
      fromName: userName || "",
      fromAvatar: userAvatar || "",
      screen: "admin_quests",
    });
  }

  if (didAwardXp) {
    try {
      await addDoc(collection(db, "users", userId, "xpAdjustments"), {
        amount: Number(xpReward),
        type: "earn",
        source: "assignment",
        note: promptTitle ? `Assignment: ${promptTitle}` : "Assignment submitted",
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("[submitWeeklyQuest] xpAdjustments log write failed:", err);
    }
  }

  return didAwardXp;
}

export async function coachSubmitWeeklyQuest({ promptId, userId, userName, userAvatar, programTag, promptTitle, responseText, xpReward, coachName }) {
  const responseId = `${promptId}_${userId}`;
  const responseRef = doc(db, "weeklyQuestResponses", responseId);
  const snap = await getDoc(responseRef);

  const data = {
    promptId,
    promptTitle: promptTitle || "",
    userId,
    userName: userName || "",
    userAvatar: userAvatar || "",
    tag: programTag || "",
    responseText: responseText || "",
    isSubmitted: true,
    isPrivate: false,
    status: "submitted",
    submittedByCoach: true,
    coachNote: `Submitted by coach${coachName ? ` (${coachName})` : ""} on behalf of client`,
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (!snap.exists()) {
    data.xpAwarded = false;
    data.xpAwardedAt = null;
    data.createdAt = serverTimestamp();
    await setDoc(responseRef, data);
  } else {
    await updateDoc(responseRef, data);
  }

  // Award XP if not already awarded
  const existingData = snap.exists() ? snap.data() : {};
  if (!existingData.xpAwarded && Number(xpReward || 0) > 0) {
    await updateDoc(responseRef, { xpAwarded: true, xpAwardedAt: serverTimestamp() });
    await updateDoc(doc(db, "users", userId), {
      xpAdjustmentTotal: increment(Number(xpReward)),
    });
    await addDoc(collection(db, "users", userId, "xpAdjustments"), {
      amount: Number(xpReward),
      type: "earn",
      source: "assignment",
      note: promptTitle ? `Assignment: ${promptTitle}` : "Assignment submitted (coach)",
      createdAt: serverTimestamp(),
    });
  }
}

export async function markPromptMissedIfNeeded(prompt, user) {
  const responseId = `${prompt.id}_${user.uid}`;
  const responseRef = doc(db, "weeklyQuestResponses", responseId);
  const snap = await getDoc(responseRef);

  const promptEnded = prompt.endAt?.toDate?.() < new Date();
  if (!promptEnded) return;

  if (!snap.exists()) {
    await setDoc(responseRef, {
      promptId: prompt.id,
      userId: user.uid,
      userName: user.name || "",
      userAvatar: user.avatar || "",
      tag: user.programTag || "",

      responseText: "",
      isSubmitted: false,
      isPrivate: false,
      status: "missed",

      xpAwarded: false,
      xpAwardedAt: null,
      submittedAt: null,

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return;
  }

  const data = snap.data();
  if (!data.isSubmitted && data.status !== "missed") {
    await updateDoc(responseRef, {
      status: "missed",
      updatedAt: serverTimestamp(),
    });
  }
}

export async function deleteWeeklyQuestPrompt(promptId) {
  await deleteDoc(doc(db, "weeklyQuestPrompts", promptId));
}

export async function updateWeeklyQuestPrompt(promptId, { title, prompt, tag, startDate, endDate, xpReward, commentsEnabled }) {
  const cleanTag = String(tag || "").trim();
  await updateDoc(doc(db, "weeklyQuestPrompts", promptId), {
    title,
    prompt,
    tag: cleanTag,
    tagKey: normalizeTag(cleanTag),
    startAt: Timestamp.fromDate(new Date(startDate)),
    endAt: Timestamp.fromDate(new Date(endDate)),
    xpReward: Number(xpReward || 0),
    commentsEnabled: !!commentsEnabled,
  });
}

export async function getAllPromptsForTag(userTag) {
  const userTagKey = normalizeTag(userTag);
  if (!userTagKey) return [];

  const snap = await getDocs(collection(db, "weeklyQuestPrompts"));

  const prompts = snap.docs
    .map((d) => ({
      id: d.id,
      ...d.data(),
    }))
    .filter((prompt) => normalizeTag(prompt.tagKey || prompt.tag) === userTagKey);

  prompts.sort((a, b) => {
    const aTime = a.startAt?.toDate?.()?.getTime?.() || 0;
    const bTime = b.startAt?.toDate?.()?.getTime?.() || 0;
    return bTime - aTime;
  });

  return prompts;
}

export async function getUserResponseForPrompt(promptId, userId) {
  const responseId = `${promptId}_${userId}`;
  const ref = doc(db, "weeklyQuestResponses", responseId);
  const snap = await getDoc(ref);

  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function addWeeklyQuestComment({
  promptId,
  responseId,
  postOwnerId,
  currentUser,
  text,
  promptTitle = "",
}) {
  const cleanText = text.trim();
  if (!cleanText) return;

  await addDoc(collection(db, "weeklyQuestComments"), {
    promptId,
    responseId,
    postOwnerId,
    authorId: currentUser.uid,
    authorName: currentUser.name || "",
    authorAvatar: currentUser.avatar || "",
    text: cleanText,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  if (postOwnerId && postOwnerId !== currentUser.uid) {
    writeUserNotification({
      toUserId: postOwnerId,
      type: "comment",
      message: `${currentUser.name || "Someone"} commented on your assignment${promptTitle ? ` "${promptTitle}"` : ""}`,
      fromName: currentUser.name || "",
      fromAvatar: currentUser.avatar || "",
    });
  }
}

export async function getCommentsForResponse(responseId) {
  const q = query(
    collection(db, "weeklyQuestComments"),
    where("responseId", "==", responseId)
  );

  const snap = await getDocs(q);

  const comments = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  comments.sort((a, b) => {
    const aTime = a.createdAt?.toDate?.()?.getTime?.() || 0;
    const bTime = b.createdAt?.toDate?.()?.getTime?.() || 0;
    return aTime - bTime;
  });

  return comments;
}
export async function updateWeeklyQuestResponse(responseId, updates) {
  await updateDoc(doc(db, "weeklyQuestResponses", responseId), {
    ...updates,
    isEdited: true,
    editedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateWeeklyQuestComment(commentId, updates) {
  await updateDoc(doc(db, "weeklyQuestComments", commentId), {
    ...updates,
    isEdited: true,
    editedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function toggleWeeklyQuestReaction({
  targetType,
  targetId,
  emoji,
  currentUser,
  postOwnerId = "",
  promptTitle = "",
}) {
  const reactionId = `${targetType}_${targetId}_${emoji}_${currentUser.uid}`;
  const ref = doc(db, "weeklyQuestReactions", reactionId);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    await deleteDoc(ref);
    return;
  }

  await setDoc(ref, {
    targetType,
    targetId,
    emoji,
    userId: currentUser.uid,
    userName: currentUser.name || "",
    userAvatar: currentUser.avatar || "",
    createdAt: serverTimestamp(),
  });

  // Notify target owner (response owner or comment author)
  let ownerId = postOwnerId;
  if (!ownerId) {
    if (targetType === "response") {
      const targetSnap = await getDoc(doc(db, "weeklyQuestResponses", targetId));
      ownerId = targetSnap.data()?.userId || "";
    } else if (targetType === "comment") {
      const targetSnap = await getDoc(doc(db, "weeklyQuestComments", targetId));
      ownerId = targetSnap.data()?.authorId || "";
    }
  }
  if (ownerId && ownerId !== currentUser.uid) {
    writeUserNotification({
      toUserId: ownerId,
      type: "reaction",
      message: `${currentUser.name || "Someone"} reacted ${emoji} to your ${targetType === "comment" ? "comment" : "assignment"}${promptTitle ? ` on "${promptTitle}"` : ""}`,
      fromName: currentUser.name || "",
      fromAvatar: currentUser.avatar || "",
    });
  }
}

export async function getReactionsForTarget(_targetType, targetId) {
  const q = query(
    collection(db, "weeklyQuestReactions"),
    where("targetId", "==", targetId)
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
}

export function subscribeToComments(responseId, callback) {
  const q = query(
    collection(db, "weeklyQuestComments"),
    where("responseId", "==", responseId),
    orderBy("createdAt", "asc")
  );

  return onSnapshot(q, (snap) => {
    const comments = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    callback(comments);
  }, (err) => console.error("subscribeToComments:", err));
}

export async function getGroupResponsesForPrompt(promptId, userTag) {
  const userTagKey = normalizeTag(userTag);

  const q = query(
    collection(db, "weeklyQuestResponses"),
    where("promptId", "==", promptId),
    where("status", "==", "submitted"),
    where("isPrivate", "==", false)
  );

  const snap = await getDocs(q);
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (!userTagKey) return all;

  // Filter client-side by normalized tag to handle case differences
  const tagFiltered = all.filter((r) => normalizeTag(r.tag) === userTagKey);
  if (tagFiltered.length > 0) return tagFiltered;

  // Legacy fallback: if no docs have a matching tag, return all docs for this prompt
  return all;
}

// Admin version — returns ALL submitted responses including private ones
export async function getGroupResponsesForPromptAdmin(promptId) {
  const q = query(
    collection(db, "weeklyQuestResponses"),
    where("promptId", "==", promptId),
    where("status", "==", "submitted")
  );

  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
